<template>
	<div class="main-container">
	  <div class="editor-container editor-container_classic-editor" ref="editorContainerElement">
		<div class="editor-container__editor">
		  <div ref="editorElement">
			<ckeditor 
			  v-if="editor && config" 
			  :modelValue="modelValue" 
			  :editor="editor" 
			  :config="config" 
			  @update:modelValue="$emit('update:modelValue', $event)" 
			/>
		  </div>
		</div>
	  </div>
	</div>
  </template>
  

<script setup>
import { computed, ref, onMounted } from 'vue';
import { Ckeditor } from '@ckeditor/ckeditor5-vue'; 

import {
	Font,
	FontColor,
	FontBackgroundColor,
	BalloonToolbar,
	ClassicEditor,
	AutoImage,
	Autosave,
	Base64UploadAdapter,
	BlockQuote,
	Bold,
	Essentials,
	Heading,
	ImageBlock,
	ImageCaption,
	ImageInline,
	ImageInsert,
	ImageInsertViaUrl,
	ImageResize,
	ImageStyle,
	ImageTextAlternative,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	Italic,
	Link,
	LinkImage,
	List,
	ListProperties,
	MediaEmbed,
	Mention,
	Paragraph,
	PasteFromOffice,
	SpecialCharacters,
	Table,
	TableCaption,
	TableCellProperties,
	TableColumnResize,
	TableProperties,
	TableToolbar,
	TodoList,
	Underline
} from 'ckeditor5';

import translations from 'ckeditor5/translations/ko.js';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  }
});

import 'ckeditor5/ckeditor5.css';
import '../../../styles/ckeditor-custom.scss';  

/**
 * Create a free account with a trial: https://portal.ckeditor.com/checkout?plan=free
 */
const LICENSE_KEY = 'GPL'; // or <YOUR_LICENSE_KEY>.

const isLayoutReady = ref(false);

const editor = ClassicEditor;

const config = computed(() => {
	if (!isLayoutReady.value) {
		return null;
	}

	return {
		toolbar: {
			items: [
				'heading',
				'|',
				'bold',
				'italic',
				'underline',
				'fontColor',             // 글자색 추가
        		'fontBackgroundColor',   // 배경색 추가
				'|', 
				'link',
				'insertImage',
				'mediaEmbed',
				'insertTable',
				'blockQuote',
				'|',
				'bulletedList',
				'numberedList', 
				'outdent',
				'indent'
			],
			shouldNotGroupWhenFull: false
		},
		plugins: [
			Font,                // 폰트 플러그인 추가
			FontColor,           // 글자색 플러그인 추가
			FontBackgroundColor, // 배경색 플러그인 추가
			BalloonToolbar,
			AutoImage,
			Autosave,
			Base64UploadAdapter,
			BlockQuote,
			Bold,
			Essentials,
			Heading,
			ImageBlock,
			ImageCaption,
			ImageInline,
			ImageInsert,
			ImageInsertViaUrl,
			ImageResize,
			ImageStyle,
			ImageTextAlternative,
			ImageToolbar,
			ImageUpload,
			Indent,
			IndentBlock,
			Italic,
			Link,
			LinkImage,
			List,
			ListProperties,
			MediaEmbed,
			Mention,
			Paragraph,
			PasteFromOffice,
			SpecialCharacters,
			Table,
			TableCaption,
			TableCellProperties,
			TableColumnResize,
			TableProperties,
			TableToolbar,
			TodoList,
			Underline
		],
		balloonToolbar: ['FontColor', 'FontBackgroundColor', '|','bold', 'italic', '|', 'link', '|', 'bulletedList', 'numberedList'],

		fontColor: {
		colors: [
			{
			color: '#FF0000',
			label: 'Red'
			},
			{
			color: '#00FF00',
			label: 'Green'
			},
			{
			color: '#0000FF',
			label: 'Blue'
			},
			{
			color: '#FFA500',
			label: 'Orange'
			},
			{
			color: '#000000',
			label: 'Black'
			}
		],
		columns: 5
		},
		fontBackgroundColor: {
		colors: [
			{
			color: '#FFFF00',
			label: 'Yellow'
			},
			{
			color: '#FFD700',
			label: 'Gold'
			},
			{
			color: '#00FFFF',
			label: 'Cyan'
			},
			{
			color: '#C0C0C0',
			label: 'Silver'
			},
			{
			color: '#FFFFFF',
			label: 'White'
			}
		],
		columns: 5
		},
		heading: {
			options: [
				{
					model: 'paragraph',
					title: 'Paragraph',
					class: 'ck-heading_paragraph'
				},
				{
					model: 'heading1',
					view: 'h1',
					title: 'Heading 1',
					class: 'ck-heading_heading1'
				},
				{
					model: 'heading2',
					view: 'h2',
					title: 'Heading 2',
					class: 'ck-heading_heading2'
				},
				{
					model: 'heading3',
					view: 'h3',
					title: 'Heading 3',
					class: 'ck-heading_heading3'
				},
				{
					model: 'heading4',
					view: 'h4',
					title: 'Heading 4',
					class: 'ck-heading_heading4'
				},
				{
					model: 'heading5',
					view: 'h5',
					title: 'Heading 5',
					class: 'ck-heading_heading5'
				},
				{
					model: 'heading6',
					view: 'h6',
					title: 'Heading 6',
					class: 'ck-heading_heading6'
				}
			]
		},
		image: {
			toolbar: [
				'toggleImageCaption',
				'imageTextAlternative',
				'|',
				'imageStyle:inline',
				'imageStyle:wrapText',
				'imageStyle:breakText',
				'|',
				'resizeImage'
			]
		},
		initialData:
		props.modelValue,
		language: 'ko',
		licenseKey: LICENSE_KEY,
		link: {
			addTargetToExternalLinks: true,
			defaultProtocol: 'https://',
			decorators: {
				toggleDownloadable: {
					mode: 'manual',
					label: 'Downloadable',
					attributes: {
						download: 'file'
					}
				}
			}
		},
		list: {
			properties: {
				styles: true,
				startIndex: true,
				reversed: true
			}
		},
		mention: {
			feeds: [
				{
					marker: '@',
					feed: [
						/* See: https://ckeditor.com/docs/ckeditor5/latest/features/mentions.html */
					]
				}
			]
		},
		placeholder: 'Type or paste your content here!',
		table: {
			contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
		},
		translations: [translations]
	};
});

onMounted(() => {
	isLayoutReady.value = true;
});
</script>
 